import { useMemo } from "react";

import { getUser } from "api/cookie_auth";
import { Button } from "components/button";
import type { FlatNode, OrderedGroup } from "screens/call/callscript-graph-to-nested";
import celebrateSrc from "assets/celebrate.svg";
import type { CallMode } from "screens/new-call";

import { CallSummary } from "./call-summary";
import styles from "./index.module.css";
import { CallDetails } from "./call-details";
import { SocketCuePayload } from "hooks/use-cues";

type CallCompletionOverlayProps = {
  flattenedGraph: FlatNode[];
  visitedGroups: Set<number>;
  orderedGroups: OrderedGroup[];
  completedNodeIds: Set<number>;
  triggered: Array<SocketCuePayload>;
  onClose: () => void;
  callDuration: number;
  mode: CallMode;
};

const EXCELLENT_SCORE_THRESHOLD = 80;
const GOOD_SCORE_THRESHOLD = 60;

const getEncouragementText = (score: number, name: string) => {
  if (score >= EXCELLENT_SCORE_THRESHOLD) {
    return `Great work, ${name}!`;
  }
  if (score >= GOOD_SCORE_THRESHOLD) {
    return `Good work, ${name}!`;
  }
  return `Not bad, ${name}!`;
};

type SummaryStats = {
  groupId: number;
  groupName: string;
  score: number; // 0-100
  completedNodes: number;
  totalNodes: number;
  incompleteCompliance: FlatNode[];
};

const calculateSummaryStats = (
  flattenedGraph: FlatNode[],
  orderedGroups: OrderedGroup[],
  completedNodeIds: Set<number>,
): SummaryStats[] => {
  const summaryStats: SummaryStats[] = [];

  for (const group of orderedGroups) {
    const nodesInGroup = flattenedGraph.filter((node) => node.groupId === group.id);

    if (nodesInGroup.length === 0) {
      continue;
    }

    const nonOptionNodes = nodesInGroup.filter((node) => node.type !== "option");

    let completedCount = 0;
    let totalCount = 0;
    let incompleteCompliance: FlatNode[] = [];

    // Handle non-option nodes
    if (nonOptionNodes.length > 0) {
      const completedNonOptions = nonOptionNodes.filter((node) => completedNodeIds.has(node.id));
      completedCount += completedNonOptions.length;
      totalCount += nonOptionNodes.length;

      incompleteCompliance = nonOptionNodes.filter(
        (node) => !completedNodeIds.has(node.id) && node.isCompliance,
      );
    }

    const score = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    summaryStats.push({
      groupId: group.id,
      groupName: group.name,
      score,
      completedNodes: completedCount,
      totalNodes: totalCount,
      incompleteCompliance: incompleteCompliance,
    });
  }

  return summaryStats;
};

export const CallCompletionOverlay = ({
  flattenedGraph,
  visitedGroups,
  orderedGroups,
  completedNodeIds,
  onClose,
  triggered,
  callDuration,
  mode,
}: CallCompletionOverlayProps) => {
  const user = getUser();
  const name = user?.name || "Agent";

  const { totalScore, missedCompliance } = useMemo(() => {
    const visitedOrderGroups = orderedGroups.filter((g) => visitedGroups.has(g.id));
    const summaryStats = calculateSummaryStats(
      flattenedGraph,
      visitedOrderGroups,
      completedNodeIds,
    );

    const totalScore =
      summaryStats.length > 0
        ? Math.round(
            summaryStats.reduce((sum, group) => sum + group.score, 0) / summaryStats.length,
          )
        : 0;
    const missedCompliance = summaryStats.flatMap((stat) => stat.incompleteCompliance ?? []);

    return { groupScores: summaryStats, totalScore, missedCompliance };
  }, [orderedGroups, flattenedGraph, completedNodeIds, visitedGroups]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <img alt="" className={styles.iconAgent} height={80} src={celebrateSrc} width={80} />
        {name && <p className={styles.subtitle}>{getEncouragementText(totalScore, name)}</p>}
      </div>

      <div className={styles.body}>
        <CallSummary
          missedCompliance={missedCompliance}
          totalScore={totalScore}
          triggeredCount={triggered.length}
          callDuration={callDuration}
          isListenerMode={mode === "listener"}
        />
        <CallDetails
          missedCompliance={missedCompliance}
          triggered={triggered}
          isListenerMode={mode === "listener"}
        />
      </div>

      <div className={styles.buttonContainer}>
        <Button onClick={onClose} variant="white">
          Done
        </Button>
      </div>
    </div>
  );
};
